import { Checkbox, Flex, Typography } from "antd";
import React, { useContext, useMemo, useState } from "react";
import HRadioGroup from "../../../components/custom/input/radio/HRadioGroup";
import HTextArea from "../../../components/custom/input/textarea/HTextArea";
import { AppContext } from "../../../context/AppContext";
import EmailComponent from "../../Setting/EmailComponent";
import type { ActionNodeData } from "../nodes/ActionNode";
import TimeSelector from "./TimeSelector";

const { Text } = Typography;

const DELAY_TIME_FIELDS = [
    { key: "delayDays", min: 0, max: 30, label: "Days" },
    { key: "delayHours", min: 0, max: 23, label: "Hours" },
    { key: "delayMinutes", min: 15, max: 59, label: "Minutes" },
];

export const NotifyUserActionForm: React.FC<{
    data: ActionNodeData;
    update: (d: Partial<ActionNodeData>) => void;
    staticMessage: string;
}> = ({ data, update, staticMessage }) => {
    const [appProperties] = useContext(AppContext);
    const [emailValidationErrors, setEmailValidationErrors] = useState<Record<string, string> | null>(null);

    const emailFieldConfig = useMemo(
        () => ({
            key: "additionalEmails",
            placeholder: "Enter an email address",
            onchange: (_parentKey: string, _key: string, value: string[]) => {
                update({ additionalEmails: value });
            },
        }),
        [update]
    );

    const handleRecordOwnerChange = (e: { target: { checked: boolean } }) => {
        update({ recordOwner: e.target.checked });
        setEmailValidationErrors(null);
    };
    const handleAccountOwnerChange = (e: { target: { checked: boolean } }) => {
        update({ accountOwner: e.target.checked });
        setEmailValidationErrors(null);
    };
    const handleAdditionalEmailsChange = (e: { target: { checked: boolean } }) => {
        update({
            includeAdditionalEmails: e.target.checked,
            additionalEmails: e.target.checked ? (data.additionalEmails ?? []) : [],
        });
        setEmailValidationErrors(null);
    };

    const isAdditionalEmailsEnabled = data.includeAdditionalEmails ?? false;

    return (
        <div className="d-flex flex-column gap-2">
            <div className="d-flex flex-column gap-1">
                <Text className="hs-fs-12 hs-color-BlackRussian hs-fw-500">Send Email to</Text>
                <div className="d-flex flex-column gap-3 mt-2">
                    <Checkbox
                        className="hs-fs-12"
                        checked={data.recordOwner ?? false}
                        onChange={handleRecordOwnerChange}
                    >
                        Record Owner
                    </Checkbox>
                    <Checkbox
                        className="hs-fs-12"
                        checked={data.accountOwner ?? false}
                        onChange={handleAccountOwnerChange}
                    >
                        Account Owner ({appProperties?.extensionInstalledUserEmailId || ""})
                    </Checkbox>
                    <Checkbox
                        className="hs-fs-12"
                        checked={isAdditionalEmailsEnabled}
                        onChange={handleAdditionalEmailsChange}
                    >
                        Additional Email Addresses
                    </Checkbox>
                </div>
            </div>
            {isAdditionalEmailsEnabled && (
                <div className="py-3 m-1 mt-3 mt-sm-2">
                    <EmailComponent
                        parentKey="workflowNotify"
                        emailList={data.additionalEmails ?? []}
                        field={emailFieldConfig}
                        setErrorObj={setEmailValidationErrors}
                    />
                    {emailValidationErrors?.["Email notification"] && (
                        <div className="hs-fs-12 hs-color-red mt-1">{emailValidationErrors["Email notification"]}</div>
                    )}
                </div>
            )}
            <div className="d-flex flex-column gap-1">
                <Text className="hs-fw-500 hs-fs-12 hs-color-BlackRussian p-1">Message</Text>
                <HTextArea
                    value={staticMessage}
                    readOnly
                    disabled
                    className="w-100"
                    rows={4}
                    autoSize={{ minRows: 4 }}
                />
            </div>
        </div>
    );
};

export const CreateLeadActionForm: React.FC<{ data: ActionNodeData; update: (d: Partial<ActionNodeData>) => void }> = ({
    data,
    update,
}) => (
    <div className="px-1 d-flex flex-column gap-3">
        <HRadioGroup
            groupLabel="Automatically create a new lead?"
            options={[
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
            ]}
            radioGroupProps={{
                value: data.createLead ?? "true",
                onChange: (e: { target: { value: string } }) =>
                    update({ createLead: (e.target as HTMLInputElement).value }),
            }}
        />
    </div>
);

export const DelayActionForm: React.FC<{ data: ActionNodeData; update: (d: Partial<ActionNodeData>) => void }> = ({
    data,
    update,
}) => (
    <Flex vertical gap={"small"}>
        <Text className="hs-fw-600 hs-fs-12 hs-color-BlackRussian">Duration</Text>
        <TimeSelector
            value={{
                delayDays: data.delayDays ?? 0,
                delayHours: data.delayHours ?? 0,
                delayMinutes: data.delayMinutes ?? 15,
            }}
            onChange={(val: { delayDays: number; delayHours: number; delayMinutes: number }) =>
                update({
                    delayDays: val.delayDays ?? 0,
                    delayHours: val.delayHours ?? 0,
                    delayMinutes: val.delayMinutes ?? 15,
                })
            }
            fields={DELAY_TIME_FIELDS}
        />
        <Text className="hs-fs-11 hs-color-mediumDark d-block mb-2">Delay must be between 15 minutes and 30 days.</Text>
    </Flex>
);
